import { QueryClient } from '@tanstack/react-query'
import { addMilliseconds, differenceInMilliseconds, isBefore } from 'date-fns'
import { queryKeys } from '../../TextEditor/queries/queryKeys'
import {
  type CollaborationToken,
  type CollaborationTokenCarrier,
  type DocumentId,
} from '../types'
import { getCollaborationToken } from './getProviderConfig'

const HOUR_MS = 60 * 60 * 1000

/**
 * A token is “expiring” when less than an hour or less than half of its
 * lifetime is left, whichever is shorter. In production tokens live for 12
 * hours, so the hour applies. Short-lived tokens, such as the 6 minute tokens
 * of dev environments, get half of their lifetime, so a fresh token is reused.
 */
export const isCollaborationTokenExpiring = ({
  expiresAt,
  receivedAt,
}: CollaborationTokenCarrier) => {
  if (expiresAt == null) return false

  const margin =
    receivedAt == null
      ? HOUR_MS
      : Math.min(HOUR_MS, differenceInMilliseconds(expiresAt, receivedAt) / 2)
  return isBefore(expiresAt, addMilliseconds(new Date(), margin))
}

const collaborationTokenQuery = (documentId: DocumentId) => ({
  queryKey: queryKeys.collaborationToken(documentId),
  queryFn: () => getCollaborationToken(documentId),
})

/**
 * Starts fetching the token for a single document before its editor mounts,
 * e.g. from a route loader. Pair it with `getCachedCollaborationToken`.
 */
export const prefetchCollaborationToken = (
  queryClient: QueryClient,
  documentId: DocumentId
) => queryClient.prefetchQuery(collaborationTokenQuery(documentId))

/**
 * Resolves the token for a single document, reusing a cached or in-flight
 * request unless the cached token is expiring. Use it as the editor’s
 * `externalAccessToken` function so reconnects get a fresh token.
 */
export const getCachedCollaborationToken = async (
  queryClient: QueryClient,
  documentId: DocumentId
): Promise<CollaborationToken> => {
  const query = collaborationTokenQuery(documentId)
  const cached = queryClient.getQueryData<CollaborationTokenCarrier>(
    query.queryKey
  )

  if (cached && !isCollaborationTokenExpiring(cached)) return cached.token

  const { token } = await queryClient.fetchQuery({ ...query, staleTime: 0 })
  return token
}
